import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Input, Label, Spinner } from "reactstrap";

import CatalogSidePanel from "./CatalogSidePanel";
import {
  buildCatalogPrintItemFromScannedUnit,
  buildCatalogPrintItemFromVariantSummary,
} from "../Print/catalogBarcodePrintItems";

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
};

const newScanIdempotencyKey = () =>
  `scan-${
    window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }`;

// The Print Center link carries receipt ids; very long sessions go to "Needs labels".
const MAX_LINKED_RECEIPTS = 50;

const STATUS_META = {
  received: { color: "success", label: "Received" },
  reversed: { color: "dark", label: "Reversed" },
  blocked: { color: "warning", label: "Blocked" },
  ambiguous: { color: "warning", label: "Ambiguous" },
  not_found: { color: "secondary", label: "No match" },
};

function CatalogReceivingModePanel({
  isOpen,
  onClose,
  locations,
  defaultLocationId,
  onProcessScan,
  onUndoScan,
  isProcessing,
  isUndoing,
  onOpenProduct,
  onPrintSession,
  onOpenPrintCenter,
}) {
  const inputRef = useRef(null);
  const [locationId, setLocationId] = useState("");
  const [barcodeValue, setBarcodeValue] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [sessionEntries, setSessionEntries] = useState([]);
  // Off by default: a scanned item usually already carries a usable label.
  // The choice belongs to the receiving session: it survives closing and
  // reopening the panel (the session does too) and only "Clear session" or
  // the operator changes it. Serialized receipts need labels regardless.
  const [needsLabels, setNeedsLabels] = useState(false);
  // Receipts of this session that need labels (kept beyond the 20-entry feed).
  const [labelReceipts, setLabelReceipts] = useState([]);

  const focusInput = () => {
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }, 20);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!locationId && defaultLocationId) {
      setLocationId(String(defaultLocationId));
    }
    focusInput();
  }, [defaultLocationId, isOpen, locationId]);

  const sessionSummary = useMemo(() => {
    return sessionEntries.reduce(
      (summary, entry) => {
        if (entry.status === "received" && !entry.reversedAt) {
          summary.successCount += 1;
          summary.totalUnits += entry.quantityDelta || 0;
          if (entry.variant?.id) {
            summary.uniqueVariants.add(entry.variant.id);
          }
        } else if (entry.status === "received" && entry.reversedAt) {
          summary.reversedCount += 1;
        } else {
          summary.issueCount += 1;
        }
        return summary;
      },
      {
        successCount: 0,
        totalUnits: 0,
        issueCount: 0,
        reversedCount: 0,
        uniqueVariants: new Set(),
      }
    );
  }, [sessionEntries]);

  const groupedSessionItems = useMemo(() => {
    const grouped = new Map();

    sessionEntries
      .filter(
        (entry) =>
          entry.status === "received" && !entry.reversedAt && entry.variant?.id
      )
      .forEach((entry) => {
        const key = entry.variant.id;
        const existing = grouped.get(key) || {
          variantId: entry.variant.id,
          productId: entry.product?.id || null,
          productTitle: entry.product?.title || "",
          variantTitle: entry.variant?.title || "",
          brandName: entry.product?.brandName || "",
          price: entry.variant?.price ?? null,
          inventory: entry.variant?.inventory || null,
          availableStock: entry.variant?.availableStock ?? null,
          sku: entry.variant?.sku || "",
          barcode: entry.variant?.barcode || "",
          processingMode: entry.processingMode || entry.product?.inventoryMode || "",
          quantity: 0,
        };

        existing.quantity += entry.quantityDelta || 0;
        grouped.set(key, existing);
      });

    return Array.from(grouped.values()).sort((left, right) =>
      left.productTitle.localeCompare(right.productTitle)
    );
  }, [sessionEntries]);

  // What this session's labels are: aggregate variants print their barcode once
  // per received unit; every serialized receive printed its own new unit barcode.
  const sessionPrint = useMemo(() => {
    const items = [];
    const copiesByKey = {};

    groupedSessionItems
      .filter((group) => group.processingMode !== "serialized")
      .forEach((group) => {
        const item = buildCatalogPrintItemFromVariantSummary({
          ...group,
          inventoryMode: group.processingMode,
        });
        items.push(item);
        copiesByKey[item.printKey] = group.quantity;
      });

    sessionEntries
      .filter(
        (entry) =>
          entry.status === "received" &&
          !entry.reversedAt &&
          entry.generatedInventoryUnitBarcode
      )
      .forEach((entry) => {
        const item = buildCatalogPrintItemFromScannedUnit({
          inventoryUnit: { barcode: entry.generatedInventoryUnitBarcode, status: "available" },
          variant: entry.variant,
          product: entry.product,
        });
        items.push(item);
        copiesByKey[item.printKey] = 1;
      });

    return { items, copiesByKey };
  }, [groupedSessionItems, sessionEntries]);

  const selectedLocation = useMemo(
    () =>
      (locations || []).find((entry) => String(entry.id) === String(locationId)) ||
      null,
    [locationId, locations]
  );

  const sessionStartedAt =
    sessionEntries.length > 0
      ? sessionEntries[sessionEntries.length - 1]?.processedAt || null
      : null;
  const lastScanAt = sessionEntries[0]?.processedAt || null;
  const lastSuccessfulEntry = sessionEntries.find(
    (entry) => entry.status === "received" && !entry.reversedAt
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = barcodeValue.trim();
    if (!trimmed || !locationId) {
      return;
    }

    const result = await onProcessScan?.({
      locationId: Number(locationId),
      barcode: trimmed,
      needsLabels,
      idempotencyKey: newScanIdempotencyKey(),
    });

    if (!result) {
      focusInput();
      return;
    }

    const normalizedResult = {
      ...result,
      processedAt: result.processedAt || new Date().toISOString(),
    };

    setLastResult(normalizedResult);
    setSessionEntries((current) => [normalizedResult, ...current].slice(0, 20));
    if (
      normalizedResult.status === "received" &&
      normalizedResult.receiptId &&
      (normalizedResult.needsLabels || normalizedResult.processingMode === "serialized")
    ) {
      setLabelReceipts((current) =>
        current.some((entry) => entry.receiptId === normalizedResult.receiptId)
          ? current
          : [...current, { receiptId: normalizedResult.receiptId, movementId: normalizedResult.movementId }]
      );
    }
    setBarcodeValue("");
    focusInput();
  };

  const handleUndoEntry = async (entry) => {
    if (!entry?.movementId || entry.reversedAt) {
      return;
    }

    const result = await onUndoScan?.({
      movementId: entry.movementId,
      ...(entry.generatedInventoryUnitBarcode
        ? { inventoryUnitBarcode: entry.generatedInventoryUnitBarcode }
        : {}),
    });

    if (!result) {
      focusInput();
      return;
    }

    setLastResult({
      ...entry,
      status: "reversed",
      message: "Receiving scan reversed successfully.",
      processedAt: result.processedAt || new Date().toISOString(),
      reversedAt: result.processedAt || new Date().toISOString(),
      reversalMovementId: result.movementId,
    });

    setLabelReceipts((current) => current.filter((labelEntry) => labelEntry.movementId !== entry.movementId));
    setSessionEntries((current) =>
      current.map((currentEntry) =>
        currentEntry.movementId === entry.movementId
          ? {
              ...currentEntry,
              reversedAt: result.processedAt || new Date().toISOString(),
              reversalMovementId: result.movementId,
            }
          : currentEntry
      )
    );

    focusInput();
  };

  const lastResultMeta = STATUS_META[lastResult?.status] || STATUS_META.not_found;

  return (
    <CatalogSidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Receiving mode"
      description="Choose a location once, then keep scanning. Each scan processes immediately and keeps the input ready for the next item."
      footer={
        <>
          {labelReceipts.length && onOpenPrintCenter ? (
            <Button
              color="primary"
              onClick={() =>
                onOpenPrintCenter(
                  labelReceipts.length <= MAX_LINKED_RECEIPTS
                    ? labelReceipts.map((entry) => entry.receiptId)
                    : null
                )
              }
            >
              {labelReceipts.length === 1 ? "Print 1 label" : `Print ${labelReceipts.length} labels`}
            </Button>
          ) : null}
          <Button
            color="primary"
            outline={Boolean(labelReceipts.length && onOpenPrintCenter)}
            onClick={() =>
              onPrintSession?.({
                items: sessionPrint.items,
                copiesByKey: sessionPrint.copiesByKey,
              })
            }
            disabled={!sessionPrint.items.length}
          >
            Print session
          </Button>
          <Button
            color="secondary"
            outline
            onClick={() => {
              setLastResult(null);
              setSessionEntries([]);
              setLabelReceipts([]);
              // A new session starts from the default: scanned items already carry labels.
              setNeedsLabels(false);
              focusInput();
            }}
            disabled={!sessionEntries.length}
          >
            Clear session
          </Button>
          <Button color="secondary" outline onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="catalog-print-summary">
        <div>
          <span>Received scans</span>
          <strong>{sessionSummary.successCount}</strong>
        </div>
        <div>
          <span>Issue scans</span>
          <strong>{sessionSummary.issueCount}</strong>
        </div>
        <div>
          <span>Total units</span>
          <strong>{sessionSummary.totalUnits}</strong>
        </div>
        <div>
          <span>Unique variants</span>
          <strong>{sessionSummary.uniqueVariants.size}</strong>
        </div>
        <div>
          <span>Reversed</span>
          <strong>{sessionSummary.reversedCount}</strong>
        </div>
      </div>

      <div className="catalog-receiving-panel__meta">
        <div>
          <span>Location</span>
          <strong>{selectedLocation?.name || "Choose location"}</strong>
        </div>
        <div>
          <span>Session started</span>
          <strong>{formatDateTime(sessionStartedAt)}</strong>
        </div>
        <div>
          <span>Last scan</span>
          <strong>{formatDateTime(lastScanAt)}</strong>
        </div>
      </div>

      <form className="catalog-receiving-panel__form" onSubmit={handleSubmit}>
        <div className="catalog-receiving-panel__controls">
          <div className="catalog-scan-panel__field">
            <Label for="catalog-receiving-location">Receiving location</Label>
            <Input
              id="catalog-receiving-location"
              type="select"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
              disabled={sessionEntries.length > 0}
            >
              <option value="">Choose location</option>
              {(locations || []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </Input>
          </div>
          <div className="catalog-scan-panel__field catalog-receiving-panel__labels">
            <Input
              id="catalog-receiving-needs-labels"
              type="checkbox"
              checked={needsLabels}
              onChange={(event) => {
                setNeedsLabels(event.target.checked);
                focusInput();
              }}
            />
            <Label for="catalog-receiving-needs-labels" check>
              <strong>Needs Black &amp; White labels</strong>
              <span className="catalog-ops-muted">
                Turn on when the scanned items don’t carry our label yet. Serialized items always get a new label.
              </span>
            </Label>
          </div>
          <div className="catalog-scan-panel__field">
            <Label for="catalog-receiving-barcode">Scan barcode</Label>
            <Input
              id="catalog-receiving-barcode"
              innerRef={inputRef}
              value={barcodeValue}
              onChange={(event) => setBarcodeValue(event.target.value)}
              placeholder="Scan variant barcode here"
              autoComplete="off"
              spellCheck={false}
              disabled={!locationId}
            />
          </div>
        </div>
      </form>

      {!locationId ? (
        <Alert color="secondary" className="mb-0">
          Choose the receiving location first. The scanner field will stay ready for repeated scans after that.
        </Alert>
      ) : sessionEntries.length > 0 ? (
        <Alert color="secondary" className="mb-0">
          Location is locked for this active session. Clear the session to switch receiving context.
        </Alert>
      ) : null}

      {isProcessing ? (
        <div className="catalog-scan-panel__loading">
          <Spinner color="primary" size="sm" />
          <span>Processing scan…</span>
        </div>
      ) : null}

      {lastResult ? (
        <Alert
          color={
            lastResult.status === "received"
              ? "success"
              : lastResult.status === "blocked" || lastResult.status === "ambiguous"
              ? "warning"
              : "secondary"
          }
          className="mb-0"
        >
          <div className="catalog-receiving-panel__result-topline">
            <Badge color={lastResultMeta.color} pill>
              {lastResultMeta.label}
            </Badge>
            <span>{lastResult.message}</span>
          </div>
          {lastResult.product?.title ? (
            <div className="catalog-receiving-panel__result-meta">
              <strong>{lastResult.product.title}</strong>
              {lastResult.variant?.title ? ` · ${lastResult.variant.title}` : ""}
              {lastResult.generatedInventoryUnitBarcode
                ? ` · Unit ${lastResult.generatedInventoryUnitBarcode}`
                : ""}
            </div>
          ) : null}
          {lastResult.product?.id ? (
            <Button
              color="link"
              size="sm"
              className="catalog-print-selection-button"
              onClick={() => onOpenProduct?.(lastResult)}
            >
              Open product
            </Button>
          ) : null}
        </Alert>
      ) : null}

      {lastSuccessfulEntry ? (
        <div className="catalog-receiving-panel__action-row">
          <Button
            color="secondary"
            size="sm"
            onClick={() => handleUndoEntry(lastSuccessfulEntry)}
            disabled={isUndoing}
          >
            {isUndoing ? "Undoing…" : "Undo last receive"}
          </Button>
        </div>
      ) : null}

      <div className="catalog-receiving-panel__feed">
        <div className="catalog-receiving-panel__feed-header">
          <h4>Session summary</h4>
          <p className="catalog-admin-section-copy">
            Grouped totals make it easy to review what was received and print labels for the whole session.
          </p>
        </div>

        {groupedSessionItems.length ? (
          <div className="catalog-receiving-panel__grouped-list">
            {groupedSessionItems.map((item) => (
              <div
                key={item.variantId}
                className="catalog-receiving-panel__grouped-item"
              >
                <div className="catalog-receiving-panel__feed-main">
                  <div className="catalog-receiving-panel__feed-title">
                    {item.productTitle}
                  </div>
                  <div className="catalog-ops-muted">
                    {item.variantTitle}
                    {item.brandName ? ` · ${item.brandName}` : ""}
                    {item.sku ? ` · SKU ${item.sku}` : ""}
                  </div>
                </div>
                <div className="catalog-receiving-panel__feed-side">
                  <code>{item.barcode || "No barcode"}</code>
                  <strong>{item.quantity}</strong>
                  <span className="catalog-ops-muted">
                    {item.processingMode === "serialized"
                      ? "serialized"
                      : "aggregate"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Alert color="secondary" className="mb-0">
            No received variants yet in this session.
          </Alert>
        )}
      </div>

      <div className="catalog-receiving-panel__feed">
        <div className="catalog-receiving-panel__feed-header">
          <h4>Recent scan activity</h4>
          <p className="catalog-admin-section-copy">
            Chronological scan feedback stays visible without interrupting the receiving loop.
          </p>
        </div>

        {sessionEntries.length ? (
          <div className="catalog-receiving-panel__feed-list">
            {sessionEntries.map((entry, index) => {
              const meta = STATUS_META[entry.status] || STATUS_META.not_found;

              return (
                <div
                  key={`${entry.barcode}-${entry.processedAt || index}`}
                  className="catalog-receiving-panel__feed-item"
                >
                  <div className="catalog-receiving-panel__feed-main">
                    <div className="catalog-receiving-panel__feed-topline">
                      <Badge color={meta.color} pill>
                        {entry.reversedAt ? "Reversed" : meta.label}
                      </Badge>
                      <span>{formatDateTime(entry.processedAt)}</span>
                    </div>
                    <div className="catalog-receiving-panel__feed-title">
                      {entry.product?.title || entry.barcode}
                    </div>
                    <div className="catalog-ops-muted">
                      {entry.variant?.title || entry.message}
                    </div>
                  </div>
                  <div className="catalog-receiving-panel__feed-side">
                    <code>{entry.barcode}</code>
                    {entry.status === "received" ? (
                      <strong>+{entry.quantityDelta || 1}</strong>
                    ) : null}
                    {entry.status === "received" && !entry.reversedAt ? (
                      <Button
                        color="link"
                        size="sm"
                        className="catalog-print-selection-button"
                        onClick={() => handleUndoEntry(entry)}
                        disabled={isUndoing}
                      >
                        Undo
                      </Button>
                    ) : null}
                    {entry.reversedAt ? (
                      <span className="catalog-ops-muted">
                        Reversed {formatDateTime(entry.reversedAt)}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Alert color="secondary" className="mb-0">
            No scans yet in this session.
          </Alert>
        )}
      </div>
    </CatalogSidePanel>
  );
}

export default CatalogReceivingModePanel;
